/* eslint-disable sonarjs/cognitive-complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  StyledHorizontalCarousel,
  HorizontalScroll,
  HorizontalGrid,
} from './HorizontalCarousel.style';

const useIntersectionObserver = (options = {}, defaultVisibility = false) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(defaultVisibility);

  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [containerRef?.current?.getBoundingClientRect(), options]);

  return [containerRef, isVisible];
};

const useElementIntersectionObserver = ({ elementRef, defaultVisibility = false }) => {
  const [isNotVisible, setIsNotVisible] = useState(defaultVisibility);

  const options = {
    rootMargin: '0px',
  };

  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsNotVisible(
      !entry.isIntersecting && elementRef?.current?.getBoundingClientRect()?.right < 70
    );
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (elementRef.current) observer.observe(elementRef.current);
    return () => {
      if (elementRef.current) observer.unobserve(elementRef.current);
    };
  }, [elementRef?.current?.getBoundingClientRect()]);

  return { isNotVisible };
};

const HorizontalCarousel = ({
  children,
  isArrowVisible,
  plpTileRef,
  defaultTileWidth,
  slidesToScroll,
  shouldDisplayCategoryNameOverlayOnInteraction,
  categoryName,
}) => {
  const scrollContainerRef = useRef(null);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [scrollEndlRef, isEndTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });
  const [scrolStartRef, isStartTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });
  const { isNotVisible } = useElementIntersectionObserver({ elementRef: plpTileRef });
  const shouldDisplayCategoryName =
    shouldDisplayCategoryNameOverlayOnInteraction && plpTileRef?.current && isNotVisible;

  useEffect(() => {
    if (isStartTileInView && plpTileRef) {
      setShowLeftArrow(false);
      setShowRightArrow(true);
    } else {
      setShowLeftArrow(true);
    }
  }, [isStartTileInView]);

  useEffect(() => {
    if (isEndTileInView && plpTileRef) {
      setShowRightArrow(false);
      setShowLeftArrow(true);
    } else {
      setShowRightArrow(true);
    }
  }, [isEndTileInView]);

  const onScrollNext = useCallback(() => {
    const oneTileWidth = plpTileRef?.current?.offsetWidth || defaultTileWidth;
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += slidesToScroll * oneTileWidth;
    }
  });

  const onScrollPrev = useCallback(() => {
    const oneTileWidth = plpTileRef?.current?.offsetWidth || defaultTileWidth;
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft -= oneTileWidth * slidesToScroll;
    }
  });

  return (
    <StyledHorizontalCarousel shouldDisplayCategoryName={shouldDisplayCategoryName}>
      <div className="carousel-arrows">
        {showLeftArrow && isArrowVisible ? (
          <button onClick={onScrollPrev} className="left-arrow" />
        ) : null}
      </div>
      {shouldDisplayCategoryName && (
        <div className="espot-category-name-wrapper">
          <span className="espot-category-name">{categoryName}</span>
        </div>
      )}
      <HorizontalScroll ref={scrollContainerRef}>
        <HorizontalGrid className="carousel-content">
          <div ref={scrolStartRef} />
          {children}
          <div ref={scrollEndlRef} />
        </HorizontalGrid>
      </HorizontalScroll>
      <div className="carousel-arrows">
        {showRightArrow && isArrowVisible ? (
          <button onClick={onScrollNext} className="right-arrow" />
        ) : null}
      </div>
    </StyledHorizontalCarousel>
  );
};
HorizontalCarousel.propTypes = {
  children: PropTypes.node,
  isArrowVisible: PropTypes.bool,
  plpTileRef: PropTypes.node,
  slidesToScroll: PropTypes.number,
  defaultTileWidth: PropTypes.number,
  shouldDisplayCategoryNameOverlayOnInteraction: PropTypes.bool,
  categoryName: PropTypes.string,
};
HorizontalCarousel.defaultProps = {
  children: null,
  isArrowVisible: false,
  plpTileRef: null,
  slidesToScroll: 3,
  defaultTileWidth: 283,
  shouldDisplayCategoryNameOverlayOnInteraction: false,
  categoryName: '',
};
export default HorizontalCarousel;
